/*
  This function is used to clear nested objects with a id for directus. If you pass an object with more than an id to directus, it will interpret it
  as an update or create on the object and not the relation definition. This function will flatten the object to only have the id of the nested object.
  Arrays containing objects and not ids have the issue and therefor will also be flattened.

  Sometimes you wish to not flat an object, for example when you are creating a new object with a predefined id.
  In this case you can pass the fieldsToIgnore parameter to the function. This will make sure that the object is not flattened.
  you can also pass key names of deep nested objects.

  If your field is an array of objects, you can pass the arraysToIgnore parameter to the function. This will make sure that the array is not flattened.


  Parameters:
  - `object: Record<string, unknown>`: The object to be cleaned. Id fields must be strings.
  - `fieldsToIgnore: string[]`: The keys of the fields to ignore when cleaning the object.
  - `arraysToIgnore: string[]`: The keys of the arrays to ignore when cleaning the object.
 */
export const clearNestedObjects = (
  object: Record<string, unknown>,
  fieldsToIgnore: string[] = [],
  arraysToIgnore: string[] = [],
  fieldsToDelete: string[] = [],
): object => {
  const copy = { ...object };
  fieldsToDelete.forEach((field) => {
    delete copy[field];
  });
  Object.entries(copy).forEach(([key, value]) => {
    if (!!value && fieldsToIgnore.includes(key)) {
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          copy[key] = clearArrayValues(
            value,
            key,
            fieldsToIgnore,
            arraysToIgnore,
          );
        } else {
          copy[key] = clearNestedObjects(
            value as Record<string, unknown>,
            fieldsToIgnore,
            arraysToIgnore,
          );
        }
      } else {
        copy[key] = value;
      }
    } else if (!!value && typeof value === 'object') {
      const toCleanValue = value;
      if (Array.isArray(toCleanValue)) {
        copy[key] = clearArrayValues(
          toCleanValue,
          key,
          fieldsToIgnore,
          arraysToIgnore,
        );
      } else {
        copy[key] = clearObjectValues(
          toCleanValue as Record<string, unknown>,
          fieldsToIgnore,
          arraysToIgnore,
        );
      }
    }
  });
  return copy;
};

const clearArrayValues = (
  array: unknown[],
  key: string,
  fieldsToIgnore: string[],
  arraysToIgnore: string[],
): unknown[] => {
  return array.map((arrayValue) => {
    if (!!arrayValue && typeof arrayValue === 'object') {
      if (!arraysToIgnore.includes(key) && 'id' in arrayValue) {
        return arrayValue['id'];
      } else {
        if (Array.isArray(arrayValue)) {
          clearArrayValues(arrayValue, key, fieldsToIgnore, arraysToIgnore);
        }
        return clearNestedObjects(
          arrayValue as Record<string, unknown>,
          fieldsToIgnore,
          arraysToIgnore,
        );
      }
    } else {
      return arrayValue;
    }
  });
};

const clearObjectValues = (
  object: Record<string, unknown>,
  fieldsToIgnore: string[],
  arraysToIgnore: string[],
): Record<string, unknown> | string => {
  const newNestedObject: Record<string, unknown> = {};
  if ('id' in object) {
    return object['id'] as string;
  } else {
    Object.entries(object).forEach(([nestedKey, nestedValue]) => {
      if (!!nestedValue && typeof nestedValue === 'object') {
        if (Array.isArray(nestedValue)) {
          newNestedObject[nestedKey] = clearArrayValues(
            nestedValue,
            nestedKey,
            fieldsToIgnore,
            arraysToIgnore,
          );
        } else {
          newNestedObject[nestedKey] = clearNestedObjects(
            nestedValue as Record<string, unknown>,
            fieldsToIgnore,
            arraysToIgnore,
          );
        }
      } else {
        newNestedObject[nestedKey] = nestedValue;
      }
    });
  }
  return newNestedObject;
};
